import type {CSS} from '@pinecast/styles';
import styled, {useColors} from '@pinecast/styles';
import * as React from 'react';

import Button from './Button';
import {VerticalGroup} from './Group';
import {useIsDarkMode} from './hooks/useIsDarkMode';
import type {CommonProps} from './icons/types';
import {DecorativeImage} from './Image';
import type {LocalizedReactNode} from './types';

const TitleWrapper = styled('h2', (_, designColors) => ({
  color: designColors.headingBlue,
  fontFamily: 'Monument Grotesk Bold',
  fontSize: 14,
  fontWeight: 400,
  marginBottom: 0,
  marginTop: 0,
  textAlign: 'center',
}));
const TextWrapper = styled('div', (_, designColors) => ({
  color: designColors.contentTextAccent,
  fontSize: 13,
  lineHeight: 16,
  textAlign: 'center',
}));

interface BaseProps {
  actionLabel?: LocalizedReactNode;
  actionPending?: boolean;
  /** Makes the action the primary action of the page. */
  isActionPrimary?: boolean;
  copy?: LocalizedReactNode;
  onAction?: (() => void) | string;
  style?: CSS;
  title: LocalizedReactNode;
}
interface IconProps extends BaseProps {
  icon: React.ComponentType<CommonProps>;
}
interface ImageProps extends BaseProps {
  image: {src: string; darkSrc?: string; height: number; width: number};
}

const EmptyState = ({
  actionLabel,
  actionPending = false,
  copy,
  isActionPrimary = false,
  onAction,
  style,
  title,
  ...rest
}: BaseProps | IconProps | ImageProps) => {
  const designColors = useColors();
  const darkMode = useIsDarkMode();
  let content;
  if ('icon' in rest) {
    const {icon: Icon} = rest;
    content = <Icon size={48} color={designColors.headingBlue} />;
  } else if ('image' in rest) {
    const {
      image: {src, darkSrc, height, width},
    } = rest;
    content = (
      <DecorativeImage
        src={(darkMode && darkSrc) || src}
        height={height}
        width={width}
      />
    );
  }

  return (
    <VerticalGroup
      spacing={16}
      centerChildren
      wrapperStyle={{
        maxWidth: 400,
        marginLeft: 'auto',
        marginRight: 'auto',
        padding: '12px 0',
        ...style,
      }}
    >
      {content}
      <TitleWrapper style={copy ? undefined : {fontSize: 14}}>
        {title}
      </TitleWrapper>
      {Boolean(copy) && <TextWrapper>{copy}</TextWrapper>}
      {Boolean(actionLabel && onAction) && (
        <Button
          $isPrimary={isActionPrimary}
          href={typeof onAction === 'string' ? onAction : undefined}
          onClick={typeof onAction === 'function' ? onAction : undefined}
          pending={actionPending}
          style={{marginTop: copy ? undefined : '16px'}}
        >
          {actionLabel}
        </Button>
      )}
    </VerticalGroup>
  );
};

export default EmptyState;
